// Единый адаптер капчи для обеих сборок:
//  - лендинг (Laravel Mix): обёртка resources/landing/js/captcha.js;
//  - merchant SPA (frontend/webpack): реэкспорт frontend/src/merchant/utils/captcha.js.
// Провайдер и site key приходят с бэка через window.__captcha
// (partials/captcha-config.blade.php и resources/views/merchant/app.blade.php) —
// смена провайдера в .env не требует правок в коде форм.
//
// Новый провайдер: объект в PROVIDERS ниже; полный чек-лист — в докблоке
// app/Service/Captcha/CaptchaService.php.
const cfg = window.__captcha || {};

const PROVIDERS = {
  turnstile: {
    src: 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit',
    ready: () => !!window.turnstile,
    render: (el, action) => window.turnstile.render(el, {sitekey: cfg.siteKey, action}),
    getToken: (id) => Promise.resolve(window.turnstile.getResponse(id) || null),
    reset: (id) => window.turnstile.reset(id),
  },
  recaptcha: {
    src: 'https://www.google.com/recaptcha/api.js?render=explicit',
    ready: () => !!(window.grecaptcha && window.grecaptcha.render),
    render: (el) => window.grecaptcha.render(el, {sitekey: cfg.siteKey}),
    getToken: (id) => Promise.resolve(window.grecaptcha.getResponse(id) || null),
    reset: (id) => window.grecaptcha.reset(id),
  },
  recaptcha_v3: {
    src: () => 'https://www.google.com/recaptcha/api.js?render=' + encodeURIComponent(cfg.siteKey),
    ready: () => !!(window.grecaptcha && window.grecaptcha.execute),
    // Невидимая: виджета нет, токен запрашивается на каждое действие.
    render: null,
    getToken: (id, action) => window.grecaptcha.execute(cfg.siteKey, {action}),
    reset: () => {},
  },
  hcaptcha: {
    src: 'https://js.hcaptcha.com/1/api.js?render=explicit',
    ready: () => !!window.hcaptcha,
    render: (el) => window.hcaptcha.render(el, {sitekey: cfg.siteKey}),
    getToken: (id) => Promise.resolve(window.hcaptcha.getResponse(id) || null),
    reset: (id) => window.hcaptcha.reset(id),
  },
};

let scriptPromise = null;

function loadProviderScript(provider) {
  if (scriptPromise) return scriptPromise;

  scriptPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = typeof provider.src === 'function' ? provider.src() : provider.src;
    script.async = true;
    script.defer = true;
    script.onerror = () => reject(new Error('captcha script failed to load'));
    script.onload = () => {
      // Скрипт мог загрузиться раньше, чем инициализируется его API — доопрашиваем.
      const startedAt = Date.now();
      const poll = () => {
        if (provider.ready()) {
          resolve();
        } else if (Date.now() - startedAt > 10000) {
          reject(new Error('captcha api not ready'));
        } else {
          setTimeout(poll, 50);
        }
      };
      poll();
    };
    document.head.appendChild(script);
  });

  return scriptPromise;
}

// Виджеты капчи рендерятся фиксированной шириной (Turnstile ~300px,
// reCAPTCHA/hCaptcha ~304px) и на узких экранах вылезают за форму, ломая вёрстку.
// Ужимаем виджет целиком по ширине контейнера: приём провайдеро-независимый —
// не нужно подбирать size/compact у каждого API, пропорции и кликабельность
// сохраняются.
//
// Два условия, без которых масштабирование молча не срабатывает (замерено на
// /login при экране 320px: карточка формы разъезжалась до 342px, виджет
// оставался в натуральных 300px, а лишнее прятал overflow-x: clip выше по
// дереву — со стороны выглядело как «капча не ужимается»):
//  1. host размечен `width: max-content`. У inline-block ширина по
//     shrink-to-fit ограничена контейнером, поэтому измеренная «натуральная»
//     ширина совпадала с доступной, и ранний выход natural <= available решал,
//     что ужимать нечего. max-content отвязывает измерение от контейнера.
//  2. У контейнера должно быть `contain: inline-size` (см. .captcha-slot и
//     .captcha-widget). Иначе 300px виджета уходят в min-content всей формы:
//     контейнер растягивается под виджет сам, доступной ширины снова «хватает»,
//     а карточка перестаёт адаптироваться. Containment оставляет высоту
//     контейнера от содержимого — её мы ниже задаём вручную.
function fitWidget(container, host) {
  host.style.transformOrigin = 'top left';
  host.style.width = 'max-content';
  host.style.transform = '';
  container.style.height = '';

  const available = container.clientWidth;
  const natural = host.offsetWidth;
  if (!available || !natural || natural <= available + 1) return;

  const scale = available / natural;
  host.style.transform = `scale(${scale})`;
  // transform не влияет на поток: без явной высоты под виджетом остался бы
  // «воздух» от неотмасштабированной высоты.
  container.style.height = `${Math.ceil(host.offsetHeight * scale)}px`;
}

// Пересчёт нужен не только на ресайз: виджет получает размеры асинхронно
// (iframe провайдера), а потом меняет их сам — на челлендже и на ошибке.
function watchWidgetFit(container, host) {
  const fit = () => fitWidget(container, host);
  let observer = null;

  fit();
  window.addEventListener('resize', fit);
  window.addEventListener('orientationchange', fit);
  if (window.ResizeObserver) {
    // Наблюдаем host: ResizeObserver отдаёт размеры без учёта transform,
    // поэтому наш scale не вызывает обратной связи.
    observer = new ResizeObserver(fit);
    observer.observe(host);
  }

  return () => {
    window.removeEventListener('resize', fit);
    window.removeEventListener('orientationchange', fit);
    if (observer) observer.disconnect();
  };
}

export function isCaptchaEnabled(formName) {
  return !!(cfg.provider && cfg.siteKey && PROVIDERS[cfg.provider] && cfg.forms && cfg.forms[formName]);
}

// Провайдер с видимым виджетом (нужен контейнер в DOM).
export function hasCaptchaWidget() {
  const provider = PROVIDERS[cfg.provider];
  return !!(provider && provider.render);
}

const NOOP = {
  enabled: false,
  getToken: () => Promise.resolve(null),
  reset: () => {},
  destroy: () => {},
};

// Инициализация капчи для контейнера el (null допустим для невидимых провайдеров).
// Если капча выключена (нет конфига/провайдера или форма не включена в
// captcha.forms) — возвращает no-op: формы работают как раньше.
export function initCaptcha(el, formName) {
  if (!isCaptchaEnabled(formName)) return NOOP;

  const provider = PROVIDERS[cfg.provider];
  if (provider.render && !el) return NOOP;

  let widgetId = null;
  let unwatchFit = null;
  // Скрипт провайдера грузится асинхронно, а контейнер в SPA может отпасть
  // раньше (форма закрыта/страница сменилась). Без этого флага destroy() до
  // загрузки не отменял ничего: unwatchFit ещё null, а отложенный .then рендерил
  // виджет в отсоединённый контейнер и вешал resize/orientationchange +
  // ResizeObserver навсегда — по набору на каждое открытие формы.
  let destroyed = false;

  const loaded = loadProviderScript(provider)
    .then(() => {
      if (destroyed || !provider.render) return;
      // Рендерим не в сам контейнер, а во вложенный inline-block: он обжимается
      // по натуральному размеру виджета, каким бы тот ни был у провайдера, и
      // остаётся единственным боксом, который надо масштабировать. Ширину ему
      // задаёт fitWidget (`max-content`) — там же, где измеряет.
      const host = document.createElement('div');
      host.style.display = 'inline-block';
      el.appendChild(host);
      widgetId = provider.render(host, formName);
      unwatchFit = watchWidgetFit(el, host);
    })
    .catch(() => {
      // Скрипт провайдера не загрузился (блокировщик/сеть) — форму не ломаем,
      // токен уйдёт пустым, решение примет бэкенд по своей fail-политике.
    });

  return {
    enabled: true,
    getToken: () => loaded.then(() => {
      if (destroyed) return null;
      return provider.getToken(widgetId, formName);
    }).catch(() => null),
    reset: () => {
      // Токены одноразовые: после каждой попытки отправки виджет надо сбросить.
      if (!destroyed && widgetId !== null) provider.reset(widgetId);
    },
    // Для SPA: контейнер живёт не всю жизнь страницы, слушателей ресайза за
    // собой не оставляем.
    destroy: () => {
      destroyed = true;
      if (unwatchFit) {
        unwatchFit();
        unwatchFit = null;
      }
    },
  };
}
